<template>
  <v-dialog
    v-model="dialog"
    max-width="700"
  >
    <v-card>
      <v-toolbar
        color="primary"
        theme="dark"
        dark
        flat
      >
        <v-toolbar-title class="white--text">
          Novo Vereador(a)
        </v-toolbar-title>
        <v-spacer />
        <v-btn
          icon
          :disabled="loadingSalvar"
          @click="fechar"
        >
          <v-icon class="white--text">
            close
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-6">
        <v-form
          ref="form"
          v-model="formValid"
          validate-on="input"
        >
          <div
            class="text-subtitle-1 font-weight-bold mb-2"
            color="white"
          >
            Dados do Vereador(a)
          </div>

          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.nome"
                label="Nome Completo *"
                variant="outlined"
                :disabled="loadingSalvar"
                :rules="[rules.required, rules.nome]"
                @input="normalizarNome"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.cpf"
                v-mask="'###.###.###-##'"
                label="CPF *"
                variant="outlined"
                :disabled="loadingSalvar"
                :rules="[rules.required, rules.cpf]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.email"
                label="Email *"
                variant="outlined"
                :disabled="loadingSalvar"
                :rules="[rules.required, rules.email]"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                :key="telefoneMask"
                v-model="form.telefone"
                v-mask="telefoneMask"
                label="Telefone *"
                variant="outlined"
                :disabled="loadingSalvar"
                :rules="[rules.required, rules.telefone]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.dataInicioMandato"
                label="Data de Início do Mandato *"
                type="date"
                variant="outlined"
                :max="form.dataFimMandato || undefined"
                :disabled="loadingSalvar"
                :rules="[rules.required, rules.dataInicioMandato]"
              />
            </v-col>
          
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                v-model="form.dataFimMandato"
                label="Data de Fim do Mandato *"
                type="date"
                variant="outlined"
                :min="form.dataInicioMandato || undefined"
                :disabled="loadingSalvar"
                :rules="[rules.required, rules.dataFimMandato]"
              />
            </v-col>
          </v-row>

          <v-divider class="my-6" />

          <div class="text-subtitle-1 font-weight-bold mb-2">
            Dados do Veículo (DETRAN)
          </div>

          <ModalBuscaPlacaDetran
            :key="buscaKey"
            :disabled="loadingSalvar"
            @update="onVeiculoUpdate"
          />

          <v-row
            v-if="veiculo.nomeProprietario || veiculo.cpfCnpjProprietario"
            class="mt-2"
          >
            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                label="Proprietário"
                variant="outlined"
                readonly
                :value="veiculo.nomeProprietario"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
            >
              <v-text-field
                label="CPF/CNPJ Proprietário"
                variant="outlined"
                readonly
                :value="cpfCnpjProprietarioFormatado"
              />
            </v-col>
          </v-row>

          <v-divider class="my-6" />

          <div class="text-subtitle-1 font-weight-bold mb-2">
            Documentos
          </div>

          <v-alert
            variant="outlined"
            type="info"
            class="mb-4"
          >
            <div><strong>Obrigatórios:</strong></div>
            <ol class="mb-0">
              <li>Diploma/Ata de Posse</li>
              <li>CRLV</li>
              <li>Contrato de Locação/Cessão (somente se o veículo não estiver em nome do vereador)</li>
              <li>Apólice de Seguro</li>
            </ol>
          </v-alert>

          <v-alert
            v-if="mensagemDocsObrigatorios"
            variant="outlined"
            type="warning"
            class="mb-4"
          >
            {{ mensagemDocsObrigatorios }}
          </v-alert>

          <v-row>
            <v-col
              cols="12"
              md="6"
              style="margin-top: 8px"
            >
              <UploadFile
                key="diplomaAta"
                nome="Diploma/Ata de Posse *"
                :file-type-erro="filesTypeErros.includes('diplomaAta')"
                :file-name="docs.diplomaAta?.name"
                :is-foto="false"
                :disabled="loadingSalvar"
                :type-file-required="'PDF'"
                @file="(file) => onChangeDocFile(file, 'diplomaAta')"
              />
              <div
                v-if="filesErros.includes('diplomaAta')"
                class="text-caption text-error mt-1"
              >
                Arquivo obrigatório.
              </div>
            </v-col>

            <v-col
              cols="12"
              md="6"
              style="margin-top: 8px"
            >
              <UploadFile
                key="crlv"
                nome="CRLV *"
                :file-type-erro="filesTypeErros.includes('crlv')"
                :file-name="docs.crlv?.name"
                :is-foto="false"
                :disabled="loadingSalvar"
                :type-file-required="'PDF'"
                @file="(file) => onChangeDocFile(file, 'crlv')"
              />
              <div
                v-if="filesErros.includes('crlv')"
                class="text-caption text-error mt-1"
              >
                Arquivo obrigatório.
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              cols="12"
              style="margin-top: 8px"
            >
              <UploadFile
                key="contrato"
                :nome="requiresContrato
                  ? 'Contrato de Locação/Cessão (Obrigatório) *'
                  : 'Contrato de Locação/Cessão (Opcional)'"
                :file-type-erro="filesTypeErros.includes('contrato')"
                :file-name="docs.contrato?.name"
                :is-foto="false"
                :disabled="loadingSalvar"
                :type-file-required="'PDF'"
                @file="(file) => onChangeDocFile(file, 'contrato')"
              />
              <div
                v-if="requiresContrato && filesErros.includes('contrato')"
                class="text-caption text-error mt-1"
              >
                Arquivo obrigatório.
              </div>
            </v-col>
          </v-row>

          <v-row class="mt-n2">
            <v-col cols="12">
              <div style="max-width: 50%; margin-bottom: 4px">
                <v-text-field
                  v-model="form.apoliceSeguroValidade"
                  v-mask="'##/##/####'"
                  label="Validade da Apólice (DD/MM/AAAA) *"
                  placeholder="DD/MM/AAAA"
                  variant="outlined"
                  :disabled="loadingSalvar"
                  :rules="docs.apoliceSeguro ? [rules.required, rules.mesAno] : []"
                />
              </div>
              <UploadFile
                key="apoliceSeguro"
                nome="Apólice de Seguro *"
                :file-type-erro="filesTypeErros.includes('apoliceSeguro')"
                :file-name="docs.apoliceSeguro?.name"
                :is-foto="false"
                :disabled="loadingSalvar"
                :type-file-required="'PDF'"
                @file="(file) => onChangeDocFile(file, 'apoliceSeguro')"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <v-spacer />

        <v-btn
          text
          :disabled="loadingSalvar"
          style="background: transparent !important; box-shadow: none !important; color: #f44336 !important;"
          @click="fechar"
        >
          Cancelar
        </v-btn>

        <v-btn
          color="primary"
          variant="flat"
          :disabled="!canSave"
          @click="salvar"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mask } from 'vue-the-mask';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { validateCpf } from '@/helpers/cpf.validator';
import { formatCpfCnpjDisplay } from '@/helpers/cpf-cnpj-display';

dayjs.extend(customParseFormat);
import ModalBuscaPlacaDetran from './ModalBuscaPlacaDetran.vue';
import cpvService from '@/services/cpv/cpv.service';
import UploadFile from '@/components/UploadFile.vue';
import GlobalStore from '@/store/modules/global';

export default {
  name: 'ModalNovoVereador',
  directives: { mask },
  components: { ModalBuscaPlacaDetran, UploadFile },
  props: {
    value: { type: Boolean, default: undefined },
    modelValue: { type: Boolean, default: undefined },
  },

  data() {
    return {
      loadingSalvar: false,

      formValid: false,

      form: {
        nome: '',
        cpf: '',
        telefone: '',
        email: '',
        dataInicioMandato: '',
        dataFimMandato: '',
        apoliceSeguroValidade: '',
      },

      veiculo: {
        carregado: false,
        placa: '',
        marcaModelo: '',
        cor: '',
        anoFabricacao: '',
        anoModelo: '',
        renavam: '',
        chassi: '',
        cpfCnpjProprietario: '',
        nomeProprietario: '',
      },

      docs: {
        diplomaAta: null,
        crlv: null,
        contrato: null,
        apoliceSeguro: null,
      },

      filesTypeErros: [],
      filesErros: [],

      bloqueadoLicenciamento: false,
      anoLicenciamentoPago: null,

      buscaKey: 0,

      rules: {
        required: v => !!v || 'Campo obrigatório',
        cpf: v => validateCpf(v) || 'CPF inválido',
        nome: v => /^[A-Za-zÀ-ÿ\s]+$/.test(v || '') || 'Nome inválido',
        email: v => /\S+@\S+\.\S+/.test(v || '') || 'Email inválido',
        telefone: v => {
          const digits = String(v || '').replace(/\D/g, '');
          if (digits.length < 10) return 'Telefone inválido';
          return true;
        },
        dataInicioMandato: v => {
          if (!v || !this.form.dataFimMandato) return true;
          return v <= this.form.dataFimMandato || 'A data de início não pode ser depois da data de fim';
        },
        dataFimMandato: v => {
          if (!v || !this.form.dataInicioMandato) return true;
          return v >= this.form.dataInicioMandato || 'A data de fim não pode ser antes da data de início';
        },
        mesAno: v => {
          if (!v) return 'Campo obrigatório';
          if (!/^\d{2}\/\d{2}\/\d{4}$/.test(v)) return 'Formato inválido. Use DD/MM/AAAA';
          const data = dayjs(v, 'DD/MM/YYYY', true);
          if (!data.isValid()) return 'Data inválida';
          if (data.isBefore(dayjs().startOf('day'))) return 'A data de validade não pode ser anterior ao dia atual';
          return true;
        },
      },
    };
  },

  computed: {
    dialog: {
      get() {
        if (typeof this.modelValue === 'boolean') return this.modelValue;
        return !!this.value;
      },
      set(v) {
        this.$emit('update:modelValue', v);
        this.$emit('input', v);
      },
    },

    telefoneMask() {
      const digits = String(this.form.telefone || '').replace(/\D/g, '');
      return digits.length > 10 ? '(##) #####-####' : '(##) ####-####';
    },

    cpfVereadorDigits() {
      return String(this.form.cpf || '').replace(/\D/g, '');
    },

    cpfProprietarioDigits() {
      return String(this.veiculo?.cpfCnpjProprietario || '').replace(/\D/g, '');
    },

    cpfCnpjProprietarioFormatado() {
      return formatCpfCnpjDisplay(this.veiculo?.cpfCnpjProprietario || '');
    },

    requiresContrato() {
      if (!this.veiculo?.carregado) return false;

      const cpfV = this.cpfVereadorDigits;
      const cpfP = this.cpfProprietarioDigits;

      if (!cpfV || !cpfP) return false;

      return cpfV !== cpfP;
    },

    docsObrigatoriosPendentes() {
      const pendentes = [];

      if (!this.docs.diplomaAta) pendentes.push('Diploma/Ata de Posse');
      if (!this.docs.crlv) pendentes.push('CRLV');
      if (this.requiresContrato && !this.docs.contrato) {
        pendentes.push('Contrato de Locação/Cessão');
      }
      if (!this.docs.apoliceSeguro) pendentes.push('Apólice de Seguro');

      return pendentes;
    },

    mensagemDocsObrigatorios() {
      if (!this.veiculo?.carregado) return '';

      if (this.requiresContrato && !this.docs.contrato) {
        return 'Como o veículo não está em nome do vereador, o Contrato de Locação/Cessão é obrigatório.';
      }

      if (!this.docsObrigatoriosPendentes.length) return '';

      if (this.docsObrigatoriosPendentes.length === 1) {
        return `Documento obrigatório pendente: ${this.docsObrigatoriosPendentes[0]}.`;
      }

      return `Documentos obrigatórios pendentes: ${this.docsObrigatoriosPendentes.join(', ')}.`;
    },

    docsOk() {
      const diplomaOk = !!this.docs.diplomaAta;
      const crlvOk = !!this.docs.crlv;
      const contratoOk = this.requiresContrato ? !!this.docs.contrato : true;
      const apoliceOk = !!this.docs.apoliceSeguro && !!this.form.apoliceSeguroValidade;
      return diplomaOk && crlvOk && contratoOk && apoliceOk;
    },

    canSave() {
      return (
        this.formValid === true &&
        !this.loadingSalvar &&
        this.veiculo?.carregado === true &&
        this.bloqueadoLicenciamento === false &&
        this.docsOk &&
        this.filesTypeErros.length === 0
      );
    },
  },

  watch: {
    value(v) {
      if (typeof v === 'boolean' && !v) this.resetState();
    },
    modelValue(v) {
      if (typeof v === 'boolean' && !v) this.resetState();
    },
  },

  methods: {
    showSnack(mensagem, type = 'warning') {
      GlobalStore.setSnackBar({
        show: true,
        message: mensagem,
        type,
        timeout: 4000,
      });
    },

    normalizarNome() {
      this.form.nome = (this.form.nome || '')
        .replace(/[^A-Za-zÀ-ÿ\s]/g, '')
        .replace(/\s{2,}/g, ' ')
        .trimStart();
    },

    onChangeDocFile(file, campo) {
      this.filesErros = this.filesErros.filter((f) => f !== campo);

      if (!file) {
        this.docs[campo] = null;
        this.filesTypeErros = this.filesTypeErros.filter((f) => f !== campo);
        return;
      }

      const fileName = String(file.name || '').toLowerCase();
      const mimeType = String(file.type || '').toLowerCase();

      const ok = mimeType
        ? mimeType === 'application/pdf'
        : fileName.endsWith('.pdf');

      if (ok) {
        this.docs[campo] = file;
        this.filesTypeErros = this.filesTypeErros.filter((f) => f !== campo);
      } else {
        this.docs[campo] = null;
        if (!this.filesTypeErros.includes(campo)) {
          this.filesTypeErros = [...this.filesTypeErros, campo];
        }
      }
    },

    validateDocumentos() {
      const required = ['diplomaAta', 'crlv', 'apoliceSeguro'];
      if (this.requiresContrato) required.push('contrato');

      const missing = required.filter((k) => !this.docs[k]);
      this.filesErros = missing;

      return missing.length === 0;
    },

    onVeiculoUpdate(payload) {

      const bloqueado =
        payload?.bloqueadoLicenciamento === true ||
        payload?.debitoLicenciamento === true;

      this.bloqueadoLicenciamento = bloqueado;
      this.anoLicenciamentoPago = payload?.anoLicenciamentoPago ?? null;

      const cpfCnpjProp =
        payload?.cpfCnpjProprietario ||
        payload?.cpfCnpj ||
        payload?.cpfProprietario ||
        '';

      if (bloqueado) {
        this.veiculo = {
          carregado: false,
          placa: payload?.placa || '',
          marcaModelo: '',
          cor: '',
          anoFabricacao: '',
          anoModelo: '',
          renavam: '',
          chassi: '',
          cpfCnpjProprietario: cpfCnpjProp,
          nomeProprietario: payload?.nomeProprietario || '',
        };
        return;
      }

      this.veiculo = {
        carregado: !!payload?.carregado,
        placa: payload?.placa || '',
        marcaModelo: payload?.marcaModelo || '',
        cor: payload?.cor || '',
        anoFabricacao: payload?.anoFabricacao || '',
        anoModelo: payload?.anoModelo || '',
        renavam: payload?.renavam || '',
        chassi: payload?.chassi || '',
        cpfCnpjProprietario: cpfCnpjProp,
        nomeProprietario: payload?.nomeProprietario || '',
      };
    },

    getCamaraId() {
      const token = localStorage.getItem('cpv_token');
      if (!token) return null;

      try {
        const decoded = JSON.parse(atob(token.split('.')[1]));
        return decoded?.camaraId || null;
      } catch (e) {
        return null;
      }
    },

    salvar() {
      const isValid =
        this.$refs.form && this.$refs.form.validate
          ? this.$refs.form.validate()
          : true;

      if (!isValid) return;

      if (this.bloqueadoLicenciamento) {
        this.showSnack('O veículo possui bloqueio de licenciamento.', 'warning');
        return;
      }

      if (!this.veiculo?.carregado) {
        this.showSnack('Busque e selecione um veículo válido antes de salvar.', 'warning');
        return;
      }

      if (this.filesTypeErros.length) {
        this.showSnack('Corrija os documentos com formato inválido. Apenas PDF é permitido.', 'warning');
        return;
      }

      if (!this.validateDocumentos()) {
        this.showSnack('Anexe os documentos obrigatórios para finalizar.', 'warning');
        return;
      }

      const camaraId = this.getCamaraId();
      if (!camaraId) {
        this.showSnack('Câmara inválida. Faça login novamente.', 'warning');
        return;
      }

      const payload = {
        nome: this.form.nome,
        cpf: (this.form.cpf || '').replace(/\D/g, ''),
        telefone: (this.form.telefone || '').replace(/\D/g, ''),
        email: this.form.email,
        camaraId,
        placaVeiculo: this.veiculo.placa,
        marcaModelo: this.veiculo.marcaModelo,
        cor: this.veiculo.cor,
        anoFabricacao: this.veiculo.anoFabricacao,
        anoModelo: this.veiculo.anoModelo,
        renavam: this.veiculo.renavam,
        chassi: this.veiculo.chassi,
        dataInicioMandato: this.form.dataInicioMandato,
        dataFimMandato: this.form.dataFimMandato,
        cpfCnpjProprietario: this.veiculo.cpfCnpjProprietario,
        dataValidadeApolice: this.form.apoliceSeguroValidade
          ? dayjs(this.form.apoliceSeguroValidade, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : null,
      };

      const formData = new FormData();

      formData.append(
        'payload',
        new File([JSON.stringify(payload)], 'payload.json', { type: 'application/json' })
      );

      formData.append('diplomaDocumento', this.docs.diplomaAta, this.docs.diplomaAta.name);
      formData.append('crlvDocumento', this.docs.crlv, this.docs.crlv.name);

      if (this.docs.contrato) {
        formData.append('locacaoDocumento', this.docs.contrato, this.docs.contrato.name);
      }

      formData.append('apoliceSeguroDocumento', this.docs.apoliceSeguro, this.docs.apoliceSeguro.name);

      this.loadingSalvar = true;

      cpvService.createVereador(formData).subscribe(
        () => {
          this.loadingSalvar = false;
          this.showSnack('Vereador(a) cadastrado(a) com sucesso.', 'success');
          this.dialog = false;
          this.$emit('refresh');
          this.resetState();
        },
        (err) => {
          this.loadingSalvar = false;
          const message = err?.response?.data?.message || 'Falha ao cadastrar. Verifique os dados e tente novamente.'
          console.log(err.response)
          this.showSnack(message, 'warning');
        }
      );
    },

    fechar() {
      this.dialog = false;
      this.resetState();
    },

    resetState() {
      this.loadingSalvar = false;
      this.formValid = false;

      this.form = {
        nome: '',
        cpf: '',
        telefone: '',
        email: '',
        dataInicioMandato: '',
        dataFimMandato: '',
        apoliceSeguroValidade: '',
      };

      this.veiculo = {
        carregado: false,
        placa: '',
        marcaModelo: '',
        cor: '',
        anoFabricacao: '',
        anoModelo: '',
        renavam: '',
        chassi: '',
        cpfCnpjProprietario: '',
        nomeProprietario: '',
      };

      this.docs = {
        diplomaAta: null,
        crlv: null,
        contrato: null,
        apoliceSeguro: null,
      };

      this.filesTypeErros = [];
      this.filesErros = [];

      this.bloqueadoLicenciamento = false;
      this.anoLicenciamentoPago = null;

      this.buscaKey += 1;

      if (this.$refs.form) {
        this.$nextTick(() => {
          if (this.$refs.form?.resetValidation) {
            this.$refs.form.resetValidation();
          }
        });
      }
    },
  },
};
</script>
